import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../common/Reveal";
import { SectionEyebrow } from "../common/SectionEyebrow";
import { useLang } from "../../i18n/LanguageContext";
import { EventCard } from "../cards/EventCard";
import { getEvents } from "../../api/events";

export const EventsPreview = () => {
  const { t, lang } = useLang();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await getEvents();

        const now = new Date();

        /*
         * Accueil :
         * - uniquement les événements en cours ou à venir
         * - triés du plus proche au plus éloigné
         * - maximum 3 événements
         */
        const upcomingEvents = data
          .filter((event) => {
            if (!event.date_debut) {
              return false;
            }

            const eventDate = new Date(event.date_debut);

            return (
              event.statut === "A_VENIR" ||
              event.statut === "EN_COURS" ||
              eventDate >= now
            );
          })
          .sort(
            (a, b) =>
              new Date(a.date_debut).getTime() -
              new Date(b.date_debut).getTime()
          )
          .slice(0, 3);

        setEvents(upcomingEvents);
      } catch (error) {
        console.error("Unable to load events.", error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  return (
    <section
      className="dmi-section bg-dmi-charcoal text-white relative overflow-hidden"
      data-testid="home-events"
    >
      {/* Effet de fond */}
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.6),transparent_40%)]" />

      <div className="dmi-container relative">

        {/* En-tête */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <SectionEyebrow>
                {t("home.eventsEyebrow")}
              </SectionEyebrow>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 className="dmi-h2 mt-6 max-w-3xl">
                {t("home.eventsTitle")}
              </h2>
            </Reveal>
          </div>

          {/* Voir tous les événements */}
          <Reveal delay={0.2}>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 text-sm tracking-wide text-white hover:text-dmi-gold transition-colors group"
              data-testid="home-events-cta"
            >
              <span className="underline-grow">
                {t("cta.seeAll")}
              </span>

              <ArrowUpRight
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </Link>
          </Reveal>
        </div>

        {/* Chargement */}
        {loading && (
          <div className="py-12 text-center text-sm text-white/50">
            {lang === "fr"
              ? "Chargement des événements..."
              : "Loading events..."}
          </div>
        )}

        {/* Événements */}
        {!loading && events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event, index) => (
              <Reveal
                key={event.id}
                delay={index * 0.1}
              >
                <EventCard
                  event={event}
                  dark
                />
              </Reveal>
            ))}
          </div>
        )}

        {/* Aucun événement */}
        {!loading && events.length === 0 && (
          <div className="py-12 text-center text-sm text-white/50">
            {lang === "fr"
              ? "Aucun événement à venir pour le moment."
              : "No upcoming events at the moment."}
          </div>
        )}

      </div>
    </section>
  );
};